import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';

// The numeric PIN pad, shared by every surface that asks for a staff PIN.
//
// It exists as its own component because there are now two such surfaces with
// different jobs - the register lock screen (authentication: a PIN that resumes
// a session) and the till's "who is serving" bar (attribution: a PIN that only
// says whose name goes on a sale) - and a second hand-rolled pad would let the
// 4-6 digit rule, the lockout countdown and the keyboard handling drift apart
// between them. The server keeps one PIN policy in IStaffPinResolver; this is
// the client half of the same idea.
//
// It owns PIN ENTRY and nothing else. It never calls an endpoint and never
// decides what a PIN means: it emits `pin-submit` and waits to be told. The host
// runs its own request and calls `reject()` on failure, which is what keeps the
// same pad usable for two operations with completely different outcomes.
//
// Theming is by custom property rather than by a variant flag, because the two
// hosts are not light-and-dark versions of one look - the lock screen is a
// full-viewport dark panel and the till bar is a dialog on an ordinary page.
// The defaults suit a normal surface; the lock screen overrides them.
@customElement('pin-keypad')
export class PinKeypad extends LitElement {
  // Shortest and longest PIN this pad accepts, mirroring the server's 4-6 digit
  // rule in IStaffPinResolver. Overridable so the two never silently disagree
  // if the server's policy moves - but the default IS the server's policy.
  @property({ type: Number, attribute: 'min-length' }) minLength = 4;
  @property({ type: Number, attribute: 'max-length' }) maxLength = 6;

  // The host is mid-request. Blocks entry so a double-tap cannot submit twice.
  @property({ type: Boolean }) busy = false;

  // The submit button's label. The pad is deliberately unopinionated about the
  // verb: "Unlock" and "Set who is serving" are genuinely different acts.
  @property({ type: String, attribute: 'submit-label' }) submitLabel =
    'Continue';

  // Explanatory line shown above the pad. Optional.
  @property({ type: String }) hint = '';

  @state() private _pin = '';
  @state() private _error: string | null = null;
  // Epoch ms when a server lockout ends; 0 when not locked out. Held as an
  // instant rather than a remaining count so a re-render cannot restart it.
  @state() private _lockedUntil = 0;
  @state() private _now = Date.now();
  @state() private _shake = false;

  // The focusable wrapper carrying the keydown handler. A counter tablet often
  // has a USB numpad, so typing digits must work without clicking the pad
  // first - which only happens while this element holds focus.
  @query('.wrap') private _wrap!: HTMLElement | null;

  private _tick?: ReturnType<typeof setInterval>;

  connectedCallback() {
    super.connectedCallback();
    // Drives the lockout countdown. Runs unconditionally because a lockout can
    // start at any time; the callback is a single state write and the element
    // is short-lived on both hosts.
    this._tick = setInterval(() => (this._now = Date.now()), 500);
  }

  disconnectedCallback() {
    if (this._tick) clearInterval(this._tick);
    super.disconnectedCallback();
  }

  // True while a server lockout is still running. Exposed because hosts need to
  // describe the state around the pad, not only inside it.
  get lockedOut(): boolean {
    return this._lockedUntil > this._now;
  }

  private get _lockRemainingSeconds(): number {
    return Math.max(0, Math.ceil((this._lockedUntil - this._now) / 1000));
  }

  // Take focus so keyboard entry works immediately. Called by the host rather
  // than on first paint, because a pad inside a dialog must claim focus when the
  // dialog opens - which is not when the element is created.
  focusPad(): void {
    this._wrap?.focus();
  }

  // Wipe the entry without reporting anything. For a host reopening the pad.
  clear(): void {
    this._pin = '';
    this._error = null;
    this._shake = false;
  }

  // The host's answer to a `pin-submit` it could not honour. Clearing the PIN on
  // every rejection is deliberate: a rejected PIN is almost always a mistyped
  // one, and leaving the wrong digits in place invites a second attempt against
  // a lockout counter that only allows five.
  reject(message: string, lockedSeconds?: number): void {
    this._error = message;
    if (lockedSeconds && lockedSeconds > 0) {
      this._lockedUntil = Date.now() + lockedSeconds * 1000;
    }
    this._pin = '';
    this._shake = true;
    setTimeout(() => (this._shake = false), 450);
  }

  private _press(digit: string) {
    if (this.busy || this.lockedOut) return;
    if (this._pin.length >= this.maxLength) return;
    this._error = null;
    this._pin += digit;
  }

  private _backspace() {
    if (this.busy || this.lockedOut) return;
    this._pin = this._pin.slice(0, -1);
  }

  private _clearPressed() {
    if (this.busy || this.lockedOut) return;
    this.clear();
  }

  private _submit() {
    if (this.busy || this.lockedOut) return;
    if (this._pin.length < this.minLength) return;
    this.dispatchEvent(
      new CustomEvent<{ pin: string }>('pin-submit', {
        detail: { pin: this._pin },
        bubbles: true,
        composed: true,
      })
    );
  }

  private _keydown = (e: KeyboardEvent) => {
    if (e.key >= '0' && e.key <= '9') this._press(e.key);
    else if (e.key === 'Backspace') this._backspace();
    else if (e.key === 'Enter') this._submit();
  };

  render() {
    const dots = Array.from(
      { length: this.maxLength },
      (_, i) => i < this._pin.length
    );
    const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];
    const blocked = this.busy || this.lockedOut;

    return html`
      <div
        class="wrap ${this._shake ? 'shake' : ''}"
        tabindex="0"
        @keydown=${this._keydown}
      >
        ${this.hint ? html`<p class="hint">${this.hint}</p>` : nothing}

        <div class="dots" role="status" aria-label="PIN length">
          ${dots.map((filled) => html`<span class=${filled ? 'on' : ''}></span>`)}
        </div>

        <div class="msg ${this.lockedOut ? 'lock' : this._error ? 'err' : ''}">
          ${
            this.lockedOut
              ? html`Too many attempts. Try again in
                ${this._lockRemainingSeconds}s.`
              : (this._error ?? html`&nbsp;`)
          }
        </div>

        <div class="pad">
          ${keys.map(
            (k) =>
              html`<button
                class="key"
                ?disabled=${blocked}
                @click=${() => this._press(k)}
              >
                ${k}
              </button>`
          )}
          <button
            class="key sub"
            ?disabled=${blocked}
            @click=${this._clearPressed}
          >
            Clear
          </button>
          <button
            class="key"
            ?disabled=${blocked}
            @click=${() => this._press('0')}
          >
            0
          </button>
          <button
            class="key sub"
            ?disabled=${blocked}
            @click=${this._backspace}
          >
            <sl-icon name="backspace"></sl-icon>
          </button>
        </div>

        <sl-button
          class="go"
          variant="primary"
          size="large"
          ?loading=${this.busy}
          ?disabled=${this._pin.length < this.minLength || this.lockedOut}
          @click=${this._submit}
        >
          ${this.submitLabel}
        </sl-button>
      </div>
    `;
  }

  static styles = css`
    :host {
      display: block;
      /* Defaults target an ordinary light surface (a dialog on a page). The
         lock screen overrides every one of these for its dark panel. */
      --pin-keypad-color: var(--app-text, #1a2b47);
      --pin-keypad-muted: var(--app-text-muted, #64748b);
      --pin-keypad-accent: var(--sl-color-primary-600, #d05f27);
      --pin-keypad-key-bg: var(--sl-color-neutral-50, #f8fafc);
      --pin-keypad-key-border: var(--app-border, #e2e8f0);
      --pin-keypad-key-hover-bg: var(--sl-color-neutral-100, #f1f5f9);
      --pin-keypad-dot-border: var(--sl-color-neutral-400, #94a3b8);
      --pin-keypad-error: var(--sl-color-danger-600, #c2410c);
      --pin-keypad-warning: var(--sl-color-warning-600, #b45309);
      --pin-keypad-key-height: 62px;
    }
    .wrap {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      color: var(--pin-keypad-color);
      outline: none;
    }
    .hint {
      margin: 0 0 14px;
      font-size: 13px;
      color: var(--pin-keypad-muted);
      line-height: 1.4;
    }
    .dots {
      display: flex;
      gap: 12px;
      margin: 4px 0 10px;
    }
    .dots span {
      width: 14px;
      height: 14px;
      border-radius: 50%;
      border: 2px solid var(--pin-keypad-dot-border);
      background: transparent;
      transition: all 0.12s ease;
    }
    .dots span.on {
      background: var(--pin-keypad-accent);
      border-color: var(--pin-keypad-accent);
      transform: scale(1.08);
    }
    .msg {
      min-height: 20px;
      font-size: 12.5px;
      margin-bottom: 14px;
      color: var(--pin-keypad-muted);
    }
    .msg.err {
      color: var(--pin-keypad-error);
    }
    .msg.lock {
      color: var(--pin-keypad-warning);
    }
    .pad {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 12px;
      width: 100%;
    }
    .key {
      appearance: none;
      border: 1px solid var(--pin-keypad-key-border);
      background: var(--pin-keypad-key-bg);
      color: var(--pin-keypad-color);
      font-size: 24px;
      font-weight: 600;
      height: var(--pin-keypad-key-height);
      border-radius: 14px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition:
        background 0.1s ease,
        transform 0.05s ease;
      user-select: none;
    }
    .key:hover:not(:disabled) {
      background: var(--pin-keypad-key-hover-bg);
    }
    .key:active:not(:disabled) {
      transform: scale(0.96);
      background: var(--pin-keypad-accent);
      color: #fff;
    }
    .key:disabled {
      opacity: 0.4;
      cursor: default;
    }
    .key.sub {
      font-size: 15px;
      font-weight: 500;
      color: var(--pin-keypad-muted);
    }
    .go {
      width: 100%;
      margin-top: 16px;
    }
    .shake {
      animation: shake 0.4s;
    }
    @keyframes shake {
      0%,
      100% {
        transform: translateX(0);
      }
      20%,
      60% {
        transform: translateX(-8px);
      }
      40%,
      80% {
        transform: translateX(8px);
      }
    }
  `;
}

declare global {
  interface HTMLElementTagNameMap {
    'pin-keypad': PinKeypad;
  }
}
